import { useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { validateInput, sanitizeEmail } from '@/utils/sanitizer';
import { useEnhancedRateLimit } from '@/hooks/useEnhancedRateLimit';
import { logger } from '@/utils/logger';

export interface UserProfile {
  id: string;
  user_id: string;
  nome: string;
  plano: 'free' | 'basic' | 'pro' | 'premium';
  is_admin?: boolean;
  data_assinatura: string;
  created_at: string;
  updated_at: string;
  referral_code?: string;
  is_on_trial?: boolean;
  trial_ends_at?: string;
  onboarding_completed?: boolean;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const { toast } = useToast();
  const authRateLimit = useEnhancedRateLimit({ maxRequests: 5, windowMs: 300000 }); // 5 attempts per 5 minutes

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setSessionStartTime(new Date());
          setTimeout(() => {
            fetchUserProfile(session.user.id);
          }, 0);
          
          // Audit log for user session
          logger.audit('User session started', { 
            userId: session.user.id, 
            timestamp: new Date().toISOString() 
          });
        } else {
          setProfile(null);
          setSessionStartTime(null);
        }
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserProfile(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setProfile(data);
      } else {
        // Profile doesn't exist, let the trigger create it
        // Wait a moment and try again
        setTimeout(async () => {
          const { data: retryData } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', userId)
            .maybeSingle();
          
          if (retryData) {
            setProfile(retryData);
          } else {
            // Still no profile, set a default to prevent crashes
            setProfile({
              id: '',
              user_id: userId,
              nome: user?.user_metadata?.nome || user?.email || 'Usuário',
              plano: 'free',
              is_admin: false,
              data_assinatura: new Date().toISOString(),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          }
        }, 500);
      }
    } catch (error) {
      logger.error('Error fetching user profile:', error);
      // Set a default profile to prevent crashes
      setProfile({
        id: '',
        user_id: userId,
        nome: user?.user_metadata?.nome || user?.email || 'Usuário',
        plano: 'free',
        is_admin: false,
        data_assinatura: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    }
  };

  // Leaked password protection using Have I Been Pwned (k-Anonymity API)
  const sha1 = async (str: string) => {
    const buffer = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(str));
    return Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  };

  const checkPasswordBreached = async (
    password: string
  ): Promise<{ ok: boolean; count?: number }> => {
    try {
      const hash = await sha1(password);
      const prefix = hash.slice(0, 5);
      const suffix = hash.slice(5);
      const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
        headers: {
          'Add-Padding': 'true',
          'User-Agent': 'LovableApp/1.0 (LeakedPasswordProtection)'
        }
      });
      if (!res.ok) return { ok: false };
      const body = await res.text();
      const match = body.split('\n').find((line) => line.startsWith(suffix + ':'));
      if (!match) return { ok: true, count: 0 };
      const count = parseInt(match.split(':')[1], 10) || 0;
      return { ok: true, count };
    } catch (e) {
      logger.warn('HIBP check failed', e, true); // Mark as sensitive
      return { ok: false };
    }
  };

  const signUp = async (email: string, password: string, nome: string) => {
    return await authRateLimit.executeWithRateLimit(async () => {
      try {
        // Input validation and sanitization
        const sanitizedEmail = sanitizeEmail(email);
        if (!sanitizedEmail) {
          throw new Error('Email inválido');
        }
        
        const nomeValidation = validateInput(nome, 100);
        if (!nomeValidation.isValid) {
          throw new Error(`Nome inválido: ${nomeValidation.errors.join(', ')}`);
        }
        
        if (password.length < 8) {
          throw new Error('Senha deve ter pelo menos 8 caracteres');
        }
        
        // Leaked password protection (HIBP)
        const hibp = await checkPasswordBreached(password);
        if (hibp.ok && (hibp.count ?? 0) > 0) {
          const err = new Error('Senha comprometida');
          toast({
            title: "Senha insegura",
            description: `Sua senha já apareceu em vazamentos (${hibp.count}x). Escolha outra senha forte.`,
            variant: "destructive",
          });
          return { error: err };
        }

        const redirectUrl = `${window.location.origin}/`;
        
        const { error } = await supabase.auth.signUp({
          email: sanitizedEmail,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              nome: nomeValidation.sanitized
            }
          }
        });

        if (error) throw error;

        toast({
          title: "Cadastro realizado!",
          description: "Verifique seu email para confirmar a conta.",
        });

        return { error: null };
      } catch (error: any) {
        const errorMessage = error.message === 'User already registered' 
          ? 'Este email já está cadastrado. Tente fazer login.'
          : error.message;
        
        toast({
          title: "Erro no cadastro",
          description: errorMessage,
          variant: "destructive",
        });
        
        return { error };
      }
    }) || { error: new Error('Rate limit exceeded') };
  };

  const signIn = async (email: string, password: string) => {
    return await authRateLimit.executeWithRateLimit(async () => {
      try {
        // Input validation and sanitization
        const sanitizedEmail = sanitizeEmail(email);
        if (!sanitizedEmail) {
          throw new Error('Email inválido');
        }
        
        const { error } = await supabase.auth.signInWithPassword({
          email: sanitizedEmail,
          password,
        });

        if (error) throw error;

        toast({
          title: "Login realizado!",
          description: "Bem-vindo de volta!",
        });

        return { error: null };
      } catch (error: any) {
        const errorMessage = error.message === 'Invalid login credentials'
          ? 'Email ou senha incorretos.'
          : error.message;
        
        toast({
          title: "Erro no login",
          description: errorMessage,
          variant: "destructive",
        });
        
        return { error };
      }
    }) || { error: new Error('Rate limit exceeded') };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      toast({
        title: "Logout realizado",
        description: "Até logo!",
      });
    } catch (error: any) {
      toast({
        title: "Erro no logout",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const resetPassword = async (email: string) => {
    return await authRateLimit.executeWithRateLimit(async () => {
      try {
        // Input validation and sanitization
        const sanitizedEmail = sanitizeEmail(email);
        if (!sanitizedEmail) {
          throw new Error('Email inválido');
        }
        
        const { error } = await supabase.auth.resetPasswordForEmail(sanitizedEmail, {
          redirectTo: `${window.location.origin}/reset-password`,
        });

        if (error) throw error;

        toast({
          title: "Email enviado!",
          description: "Verifique sua caixa de entrada para redefinir a senha.",
        });

        return { error: null };
      } catch (error: any) {
        toast({
          title: "Erro ao enviar email",
          description: error.message,
          variant: "destructive",
        });
        
        return { error };
      }
    }) || { error: new Error('Rate limit exceeded') };
  };

  // Session security monitoring
  const getSessionInfo = () => ({
    startTime: sessionStartTime,
    duration: sessionStartTime ? Date.now() - sessionStartTime.getTime() : 0,
    isValid: !!session && !!user
  });

  return {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    sessionInfo: getSessionInfo(),
    authRateLimitStatus: authRateLimit.getStatus()
  };
};